import { useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { StyledIconButton, TextWithSpan, UploadFileBox, VisuallyHiddenInput } from '../styles';
import theme from '@/theme';
import { useController, Control } from 'react-hook-form';
import { AccountSettingsSchema } from '../../schema';
import heic2any from 'heic2any';
import { MyAvatar } from './Avatar';
import { errorReporter } from '@/services/errorReportingService';

type UploadAvatarProps = {
  control: Control<AccountSettingsSchema>;
};

const UploadAvatar = ({ control }: UploadAvatarProps) => {
  const { field, fieldState } = useController({
    name: 'avatar',
    control,
  });

  const [error, setError] = useState<string | null>(null);

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) {
      setError('Something went wrong while uploading the file. Please try again.');
      return;
    }

    setError(null);

    try {
      if (file.type === 'image/heic') {
        const jpgBlob = await heic2any({ blob: file, toType: 'image/jpeg' });
        // Ensure jpgBlob is a single Blob and not an array
        const singleBlob = Array.isArray(jpgBlob) ? jpgBlob[0] : jpgBlob;
        // Convert singleBlob to a File for preview compatibility
        const jpgFile = new File([singleBlob], 'avatar.jpg', { type: 'image/jpeg' });

        field.onChange(jpgFile);
      } else {
        field.onChange(file);
      }
    } catch (err) {
      errorReporter.expected('Failed to convert HEIC avatar to JPG', err, {
        feature: 'settings',
        operation: 'heicConvert',
      });
      setError('Failed to convert HEIC to JPG.');
    }
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    const { errors } = fileRejections[0];
    if (errors[0].code === 'too-many-files') {
      setError('You can only upload one file at a time.');
    } else if (errors[0].code === 'file-too-large') {
      setError('File is too large. Max 10 MB allowed.');
    } else if (errors[0].code === 'file-invalid-type') {
      setError('File type is not supported. Please upload JPG, PNG, or HEIC file.');
    } else {
      setError('Something went wrong while uploading the file. Please try again.');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/heic': ['.heic'],
    },
    maxSize: 10 * 1024 * 1024,
    maxFiles: 1,
    onDrop,
    onDropRejected,
  });

  return (
    <>
      <Typography variant="body2" fontWeight="700" color={theme.palette.neutral.greyFont} mb="0.5rem">
        Change Avatar
      </Typography>
      <Stack flexDirection="row">
        <MyAvatar avatar={field.value} />
        <Box ml="2rem" width="100%">
          <UploadFileBox {...getRootProps({ isDragActive })}>
            <StyledIconButton>
              <UploadFileOutlinedIcon color="primary" />
            </StyledIconButton>
            <TextWithSpan variant="body1" fontWeight="500">
              <span>Click here </span>
              to upload your file, or drag and drop it here.
            </TextWithSpan>
            <Typography variant="body2" color="#94A3B8">
              Supported formats: JPG, PNG, HEIC (up to 10 MB each)
            </Typography>
            <VisuallyHiddenInput {...getInputProps()} />
          </UploadFileBox>
          {(error || fieldState.error) && <FormHelperText error>{error || fieldState.error?.message}</FormHelperText>}
        </Box>
      </Stack>
    </>
  );
};

export default UploadAvatar;
